<script module>
  export const i18n = {
    en: {
      YOUTH: 'youth',
      SENIOR: 'senior',
      ADULT: 'adult'
    },
    sv: {
      YOUTH: 'ungdom',
      SENIOR: 'senior',
      ADULT: 'vuxen',
      'Your trip': 'Din resa',
      'Price from': 'Pris från',
      Share: 'Dela',
      Copied: 'Kopierad',
      'Choose a trip': 'Välj en resa',
      Continue: 'Fortsätt'
    },
    da: {
      YOUTH: 'ungdom',
      SENIOR: 'senior',
      ADULT: 'voksen',
      'Your trip': 'Din rejse',
      'Price from': 'Pris fra',
      Share: 'Del',
      Copied: 'Kopieret',
      'Choose a trip': 'Vælg en rejse',
      Continue: 'Fortsæt'
    }
  }
</script>

<script>
  import { browser } from '$app/environment'
  import { page } from '$app/state'

  import { getPassengersLabel } from '$lib/utils/passengers.js'
  import { getCheapestPrice } from '$lib/Journey.svelte'
  import { register } from '$lib/utils/text.svelte.js'
  import { intersection } from '$lib/intersection.js'
  import State from '$lib/utils/state.svelte.js'
  import { gql } from '$lib/graphql.svelte.js'
  import Symbol from '$lib/Symbol.svelte'
  import Button from '$lib/Button.svelte'
  import { money } from '$lib/utils/money.svelte.js'
  import Map from '$lib/Map.svelte'

  let text = register(i18n)

  /**
   * @type {{
   *  origin?: Schema.Location,
   *  destination?: Schema.Location,
   *  via?: Schema.Location,
   *  passengers: Schema.PassengerPlaceholderInput[],
   *  departureDate: string,
   *  journeyOffer?: Schema.JourneyOffer,
   *  loading?: boolean
   * }}
   */
  const {
    origin,
    destination,
    via,
    passengers,
    departureDate,
    journeyOffer,
    loading
  } = $props()

  /** @type {App.GraphQLOperation<{ journey: Schema.Journey }, { id: string }>} */
  const GetPolylines = gql`
    query GetPolylines($id: ID!) {
      journey: node(id: $id) {
        ... on Journey {
          itinerary {
            ... on SegmentCollection {
              polylines
            }
          }
        }
      }
    }
  `

  $effect(() => {
    if (journeyOffer) {
      return GetPolylines.execute({ id: journeyOffer.id })
    }
  })

  let passengerLabel = $derived(getPassengersLabel(passengers))
  let price = $derived(getCheapestPrice(journeyOffer))

  let copied = $state(false)
  const onshare = async (event) => {
    const { currentTarget } = event
    if (navigator.clipboard) {
      event.preventDefault()
      await navigator.clipboard.writeText(currentTarget.href)
    } else if (window.navigator.share) {
      event.preventDefault()
      await window.navigator.share({
        title: document.title,
        url: currentTarget.href
      })
    } else {
      return
    }
    copied = true
    setTimeout(() => (copied = false), 2000)
  }

  const asTimestamp = (date) => {
    return new Date(date).toLocaleTimeString(State.language, {
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23'
    })
  }

  const asDateString = (date) => {
    return new Date(date).toLocaleDateString(State.language, {
      weekday: 'long',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  let transition = $state(0)
  const media = browser ? window.matchMedia('(width >= 1000px)') : null
  const onintersect = (entry) => {
    if (!media?.matches) {
      transition =
        1 - Math.min(1, Math.max(0, (0.15 - entry.intersectionRatio) / 0.15))
    }
  }
</script>

<fieldset class="sidebar" use:intersection={{ onintersect, once: false }}>
  <div class="header">
    <h1 class="heading">{text`Your trip`}</h1>
    <Button
      adapt
      size="xs"
      type="button"
      href={page.url}
      muted={!journeyOffer}
      --current-color="rgb(var(--rgb-muted))"
      onclick={onshare}>
      <Symbol name={copied ? 'checkmark' : 'share'} size="sm" prefix />
      {copied ? text`Copied` : text`Share`}
    </Button>
  </div>

  <div class="map">
    <Map
      locations={[origin, via, destination]
        .concat(
          // @ts-ignore
          journeyOffer?.itinerary
            ?.filter((item) => item.__typename === 'Stopover')
            .map((/** @type {Schema.Stopover} */ item) => item.location)
        )
        .filter(Boolean)}
      polylines={GetPolylines.data?.journey?.itinerary
        ?.map((/** @type {Schema.SegmentCollection} */ item) => item.polylines)
        .filter(Boolean) || []} />
  </div>

  <details class="summary" class:expandable={journeyOffer?.itinerary}>
    <summary class="toggle">
      <div>
        <strong class="title">{origin?.name} – {destination?.name}</strong>
        <br />
        <span class="departure">
          {asDateString(new Date(departureDate))},
        </span>
        {passengers.length}
        {text(passengerLabel)}
      </div>
      <div class="chevron">
        <span class="symbol">
          <Symbol name="chevron" size="sm" />
        </span>
      </div>
    </summary>
    {#if journeyOffer?.itinerary}
      {@const segments = journeyOffer.itinerary
        .filter((item) => item.__typename === 'SegmentCollection')
        .flatMap(
          (/** @type {Schema.SegmentCollection} */ item) => item.segments
        )
        .filter(Boolean)}
      <hr class="divider" />
      <ol class="itinerary">
        {#each segments as segment}
          <li>
            <span class="leg">
              {segment.origin.name} – {segment.destination.name}
            </span>
            <br />
            {asTimestamp(segment.departureAt)} – {asTimestamp(
              segment.arrivalAt
            )}, {asDateString(new Date(segment.departureAt))}
          </li>
        {/each}
      </ol>
    {/if}
  </details>

  <div
    class="footer"
    id="search-sidebar-footer"
    class:fixed={transition === 1}
    style:--transition={transition}>
    <div class="content">
      <p>
        {text`Price from`}
        <span class="price" class:placeholder={!price}>
          {#if price}
            {money(price)}
          {:else}
            {text`Choose a trip`}
          {/if}
        </span>
      </p>
      <Button
        disabled={!price || loading}
        success={Boolean(price && !loading)}
        type="submit"
        name="journeyOfferId"
        value={journeyOffer?.id}>
        {#if loading}<Symbol name="spinner" prefix />{/if}
        {text`Continue`}
      </Button>
    </div>
  </div>
</fieldset>

<style>
  .sidebar {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .heading {
    float: left;
    font-size: 1.75rem;
    font-weight: 700;
    font-family: var(--heading-font-family);
    word-spacing: -0.05em;
    letter-spacing: -0.02em;
  }

  .map {
    position: relative;
    aspect-ratio: 16/9;
    max-height: 30vh;
    width: 100%;
    border-radius: var(--border-radius-md);
    overflow: hidden;
  }

  .summary {
    padding: 0.9rem 1rem;
    position: relative;
    font-size: clamp(0.875rem, 2vw, 1rem);
    letter-spacing: 0.025em;
    line-height: 1.2;
    color: rgb(var(--rgb-muted));
    background-color: var(--background-alt);
    border-radius: var(--border-radius-md);
  }

  .toggle {
    display: flex;
    align-items: flex-start;
    user-select: none;
    cursor: pointer;
  }

  .toggle::-webkit-details-marker {
    display: none;
  }

  .chevron {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.75rem;
    height: 1.75rem;
    border: 1px solid rgba(var(--rgb-gray), 0.25);
    margin-left: auto;
    border-radius: 100%;
    background-color: var(--background);
    box-shadow: var(--box-shadow-sm);
    transition: var(--box-shadow-transition);
  }

  .summary:hover .chevron {
    box-shadow: var(--box-shadow-sm-hover);
  }

  .summary:active .chevron {
    box-shadow: var(--box-shadow-sm-active);
  }

  .summary:not(.expandable) .chevron {
    display: none;
  }

  .summary[open] .symbol {
    transform: rotate(180deg);
  }

  .title {
    color: rgb(var(--rgb-text));
  }

  .summary[open] .departure {
    display: none;
  }

  .divider {
    margin: 0.5rem 0;
  }

  .itinerary {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .leg {
    color: rgb(var(--rgb-text));
  }

  .footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .content {
    display: flex;
    justify-content: space-between;
    gap: 1rem;
    width: 100%;
    font-size: 0.9375rem;
  }

  @media (width < 1000px) {
    :global(:root[scripting-enabled]) .footer:not(.fixed) {
      width: 100vw;
      height: 4rem;
      padding: 0 var(--document-margin);
      position: fixed;
      bottom: 0;
      left: 0;
      background: var(--background);
      transform: translateY(calc(var(--transition) * 100%));
      box-shadow:
        0 -0.5px 0 0 rgba(var(--rgb-blue), 0.1),
        0 -0.125rem 0.25rem 0 rgba(var(--rgb-blue), 0.08);
    }

    :root:has(#search-sidebar-footer:not(.fixed)) {
      --current-scroll-margin-bottom: 4rem;
    }
  }

  .price {
    display: block;
    font-size: 1.5rem;
    line-height: 1;
    font-weight: bold;
  }

  .price.placeholder {
    font-size: 1rem;
    font-weight: normal;
    color: rgb(var(--rgb-muted));
  }
</style>
